<template>
  <BaseButton class="mb-4" color="default" @click="modal = true">
    <CalendarIcon class="fill-current w-5 h-5 mr-2" />
    {{ $t('checkout.add_to_calendar') }}
  </BaseButton>

  <BaseModal v-model="modal">
    <template #header>{{ $t('checkout.add_to_calendar') }}</template>

    <div class="mb-4 space-y-6 p-6 grow overflow-y-auto">
      <div
        class="space-y-4 rounded-md border border-surface-grey p-4"
        v-for="item in cart.items"
        :key="item.id"
      >
        <div class="bg-surface-light flex items-start justify-between gap-4">
          <div class="space-y-1">
            <div class="font-semibold text-sm line-clamp-1">
              {{ item.name }}
            </div>
            <div class="text-xs text-gray-500">{{ getDisplayDate(item, locale) }}</div>
            <div class="text-xs text-gray-500">{{ item.venueName }}</div>
          </div>
          <img
            :src="item.image"
            :alt="item.name"
            class="w-25 aspect-100/56 rounded-sm object-cover border border-surface-grey"
          />
        </div>

        <BaseButton color="default" size="sm" @click.prevent="addToGoogleCalendar(item.id.toString())">
          <CalendarIcon class="fill-current w-5 h-5 mr-2" />
          {{ $t('checkout.add_to_calendar') }}
        </BaseButton>
        <BaseButton color="default" size="sm" @click.prevent="addToICalendar(item.id.toString())">
          <CalendarIcon class="fill-current w-5 h-5 mr-2" />
          {{ $t('checkout.download_ics') }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { useCartStore } from '@/stores/cart';
  import { useGeneralStore } from '@/stores/general';
  import type { CalendarData, Customer, Venue } from '@/types';
  import { getDisplayDate } from '@/utils/cartItemUtil';
  import { unixtimeToISO8601 } from '@/utils/unixtimeToISO8601';
  import CalendarIcon from '../icons/CalendarIcon.vue';
  import BaseButton from '../ui/BaseButton.vue';
  import BaseModal from '../ui/BaseModal.vue'

  const modal = ref(false);

  const cart = useCartStore();
  const generalStore = useGeneralStore();
  const { locale } = useI18n();

  const venues = computed(() => generalStore.venues() as Venue[]);
  const customer = computed(() => generalStore.customer() as Customer);

  onMounted(() => {
    generalStore.fetchGeneral();
  });
  
  function addToGoogleCalendar(itemId: string) {
    // console.log('Adding to Google Calendar itemId', itemId);
    const calendarData = createCalendarData(itemId);
    if (!calendarData) return;
    // console.log('calendarData', calendarData);

    const url = 'http://www.google.com/calendar/event?' +
              'action=TEMPLATE' +
              '&text=' + encodeURIComponent(calendarData.title) +
              '&dates=' + calendarData.utcStartStr + '/' + calendarData.utcEndStr +
              '&details=' + encodeURIComponent(calendarData.description) +
              '&location=' + encodeURIComponent(calendarData.location) +
              '&trp=false' +
              '&sprop=' +
              '&sprop=name:';
    window.open(url, '_blank');
    //retain receipt url
    //cart.setReceiptURL();
  }

  function addToICalendar(itemId: string) {
    // Quick overview : https://en.wikipedia.org/wiki/ICalendar
    // Full spec      : http://tools.ietf.org/html/rfc5545

    // console.log('Downloading ICS for itemId', itemId);
    const calendarData = createCalendarData(itemId);
    if (!calendarData) return;
    // console.log('calendarData', calendarData);
    if (!calendarData) return;

    let organizerStr = '';
    if (calendarData.name != '') {
      organizerStr = 'CN=' + calendarData.name;

      if (calendarData.email != '') {
        organizerStr += ';MAILTO:' + calendarData.email;
      }
    }

    const icsUri = "data:text/calendar;charset=utf-8," +
                 encodeURIComponent(
                     "BEGIN:VCALENDAR\n" +
                     "VERSION:2.0\n" +
                     "PRODID:-//safeticket/calendarentry//NONSGML v1.0//EN\n" +
                     "BEGIN:VEVENT\n" +
                     "UID:" + calendarData.eventId + "\n" +
                     "DTSTAMP:" + calendarData.utcStartStr + "\n" +
                     "ORGANIZER:" + organizerStr + "\n" +
                     "DTSTART:" + calendarData.utcStartStr + "\n" +
                     "DTEND:" + calendarData.utcEndStr + "\n" +
                     "SUMMARY:" + calendarData.title + "\n" +
                     "END:VEVENT\n" +
                     "END:VCALENDAR");

    window.open(icsUri, '_blank');
    //retain receipt url
    //cart.setReceiptURL();
  }

  function createCalendarData(eventId: string): CalendarData | null {
    const item = cart.items.find(i => i.id.toString() === eventId);
    let calendarData: CalendarData;
    if (item) {
      let location = '';
      let description = '';
      let utcStartStr = '';
      let utcEndStr = '';
      const event = generalStore.events().find(event => event.id == item.id);
      if(event){
          const venue = venues.value.find(v => v.id === event.venue);
          if(venue){
              location += (venue['name'] == '' ? '' : (venue['name'] + ', '));
              location += (venue['address'] == '' ? '' : (venue['address'] + ', '));
              location += (venue['address2'] == '' ? '' : (venue['address2'] + ', '));
              location += (venue['zip'] == '' ? '' : (venue['zip'] + ' '));
              location += (venue['city'] == '' ? '' : (venue['city']));
          }
          description += (event.description ? event.description + '; ' : '');
          description += 'Link: ' + window.location.href;
          const eventtime = event.eventtime ? event.eventtime : 0;
          const eventtimeGmtOffset = event.eventtime_gmt_offset ? event.eventtime_gmt_offset : 0;
          utcStartStr = unixtimeToISO8601(eventtime, eventtimeGmtOffset);
          utcEndStr = unixtimeToISO8601(eventtime, eventtimeGmtOffset);
      }
      
      calendarData = {
          eventId: item.id.toString(),
          title: item.name,
          description: description,
          location: location,
          utcStartStr: utcStartStr,
          utcEndStr: utcEndStr,
          email: customer.value.email,
          name: customer.value.name,
      };
      return calendarData;
    }

    return null;
  }




</script>
